import React, { useMemo, useState } from 'react'
import {
  Modal,
  MODAL_SIZE_LABELS,
  MODAL_SIZES,
  type ModalProps,
  type ModalSize,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import {
  getModalRecipes,
  getModalShowcasePatterns,
} from './ModalShowcase.samples'
import './ModalShowcase.css'

type ModalStaticSampleProps = {
  label: string
  children: React.ReactNode
}

const ModalStaticSample: React.FC<ModalStaticSampleProps> = ({
  label,
  children,
}) => (
  <div className="modal-showcase__sample">
    <span className="modal-showcase__sample-label">{label}</span>
    {children}
  </div>
)

const SHOWCASE_PATTERNS = getModalShowcasePatterns()

const ModalShowcase: React.FC = () => {
  const [open, setOpen] = useState(true)
  const [size, setSize] = useState<ModalSize | 'recipe'>('recipe')
  const [patternId, setPatternId] = useState(
    SHOWCASE_PATTERNS[0]?.id ?? 'add-info',
  )
  const [closeOnBackdrop, setCloseOnBackdrop] = useState(true)

  const recipes = useMemo(() => getModalRecipes(), [])
  const selectedRecipe =
    recipes.find((recipe) => recipe.id === patternId) ?? recipes[0]

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Layout',
        fields: [
          {
            id: 'modal-pattern',
            label: 'Variant / pattern',
            control: (
              <select
                id="modal-pattern"
                className="ui-field-control"
                value={patternId}
                onChange={(event) => setPatternId(event.target.value)}
              >
                {SHOWCASE_PATTERNS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'modal-size',
            label: 'Size',
            control: (
              <select
                id="modal-size"
                className="ui-field-control"
                value={size}
                onChange={(event) =>
                  setSize(event.target.value as ModalSize | 'recipe')
                }
              >
                <option value="recipe">Use pattern default</option>
                {MODAL_SIZES.map((option) => (
                  <option key={option} value={option}>
                    {MODAL_SIZE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'modal-open',
            label: 'Visible',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="modal-open"
                  type="checkbox"
                  checked={open}
                  onChange={(event) => setOpen(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'modal-close-on-backdrop',
            label: 'Close on backdrop click',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="modal-close-on-backdrop"
                  type="checkbox"
                  checked={closeOnBackdrop}
                  onChange={(event) => setCloseOnBackdrop(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [closeOnBackdrop, open, patternId, size],
  )

  const livePreview =
    selectedRecipe && React.isValidElement(selectedRecipe.node)
      ? React.cloneElement(
          selectedRecipe.node as React.ReactElement<ModalProps>,
          {
            open,
            closeOnBackdrop,
            onClose: () => setOpen(false),
            ...(size === 'recipe' ? {} : { size }),
          },
        )
      : null

  return (
    <>
      <UiPreviewCanvas
        title="Modal"
        description="Centered dialog with header, body, and footer actions. Switch patterns in the properties panel to preview every modal recipe live."
        stageClassName={`ui-preview__stage--wide modal-showcase__stage-wrap`}
      >
        <div className={`modal-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Choose a variant/pattern in the properties panel — Add info, File
              details, Move files, Setup wizard, Move appointment, Import file,
              Edit photo, and more.
            </p>

            <div className={`modal-showcase__demo`}>
              <div className="modal-showcase__page" aria-hidden={open}>
                <div className="modal-showcase__page-header" />
                <div className="modal-showcase__page-grid">
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>

              {livePreview}
            </div>
          </section>

          <section className="modal-showcase__recipes">
            <h3 className="ui-showcase-live__title">Recipes</h3>
            <p className="ui-showcase-live__note">
              Real-world modal patterns from the design system: forms,
              confirmations, wizards, messaging, and scheduling flows.
            </p>
            <div className="modal-showcase__recipes-grid">
              {recipes.map((recipe) => (
                <ModalStaticSample key={recipe.id} label={recipe.label}>
                  {recipe.node}
                </ModalStaticSample>
              ))}
            </div>
          </section>

          <section className="modal-showcase__sizes">
            <h3 className="ui-showcase-live__title">Sizes</h3>
            <p className="ui-showcase-live__note">
              Static examples of the five modal widths from the design system.
            </p>
            <div className="modal-showcase__sample-grid">
              {MODAL_SIZES.map((modalSize) => (
                <ModalStaticSample
                  key={modalSize}
                  label={MODAL_SIZE_LABELS[modalSize]}
                >
                  <Modal
                    inline
                    open
                    size={modalSize}
                    title="Add info"
                    primaryLabel="Primary"
                    secondaryLabel="Secondary"
                    closeOnBackdrop={false}
                    showClose={false}
                  />
                </ModalStaticSample>
              ))}
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Modal" sections={propertySections} />
    </>
  )
}

export default ModalShowcase
